import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { elementLabel, moveElement, resizeElement, STEP, type Layout, type Media } from './layout';

interface Props {
  layout: Layout;
  media: Media;
  selected: number | null;
  onSelect: (index: number | null) => void;
  onChange: (layout: Layout) => void;
}

/** Pixels per millimetre. Clamped so a 12 mm tape is still tall enough to grab
 *  a block on, and a 119 mm box label still fits a laptop screen. */
const MIN_SCALE = 4;
const MAX_SCALE = 14;

/**
 * The label, drawn at a size a person can put a finger on.
 *
 * Not a rendering — the picture beside it is the rendering. This is a handle:
 * blocks in roughly the right places, at roughly the right sizes, so that
 * moving them is done by moving them rather than by typing coordinates and
 * waiting to see where they went.
 *
 * Pointer events rather than mouse events, and one code path for both. The
 * layouts most in need of adjustment are the 12 mm tapes, and those are
 * adjusted standing at the printer with a telephone in one hand — a canvas that
 * only answers to a mouse would be a designer for the wrong room.
 */
export function Canvas({ layout, media, selected, onSelect, onChange }: Props) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(MIN_SCALE);

  // The scale follows the width of the column it lands in, so the label is as
  // large as the screen allows on a laptop and still whole on a telephone.
  useEffect(() => {
    const element = box.current;
    if (!element) return;

    const fit = () => {
      const available = element.clientWidth;
      if (!available) return;
      const wanted = available / media.width_mm;
      setScale(Math.max(MIN_SCALE, Math.min(MAX_SCALE, wanted)));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [media.width_mm]);

  /**
   * One drag, from press to release.
   *
   * `setPointerCapture` is what makes a drag survive leaving the block — and
   * leaving the block is normal here, because a block is dragged by pushing it
   * against the edge of the label. Without capture the pointer events stop
   * arriving the moment the finger crosses the border and the block is dropped
   * where it happened to be.
   */
  const drag = (
    event: React.PointerEvent,
    index: number,
    mode: 'move' | 'resize',
  ) => {
    const element = layout.elements[index];
    if (!element) return;

    event.preventDefault();
    event.stopPropagation();
    onSelect(index);

    const startX = event.clientX;
    const startY = event.clientY;
    const origin = { x: element.x, y: element.y, w: element.w, h: element.h };
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);

    const onMove = (moved: PointerEvent) => {
      const dx = (moved.clientX - startX) / scale;
      const dy = (moved.clientY - startY) / scale;
      const next =
        mode === 'move'
          ? moveElement(element, media, { x: origin.x + dx, y: origin.y + dy })
          : resizeElement(element, media, { w: origin.w + dx, h: origin.h + dy });
      onChange({
        ...layout,
        elements: layout.elements.map((current, at) => (at === index ? next : current)),
      });
    };

    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };

    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  /** Arrow keys, for the tenth of a millimetre a finger cannot hit. */
  const nudge = (event: React.KeyboardEvent, index: number) => {
    const step = event.shiftKey ? 1 : STEP;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    const element = layout.elements[index];
    if (!move || !element) return;

    event.preventDefault();
    onChange({
      ...layout,
      elements: layout.elements.map((current, at) =>
        at === index
          ? moveElement(element, media, { x: element.x + move[0], y: element.y + move[1] })
          : current,
      ),
    });
  };

  return (
    <div className="designer__canvas-box" ref={box}>
      <div
        className="designer__canvas"
        style={{ width: media.width_mm * scale, height: media.height_mm * scale }}
        onPointerDown={() => onSelect(null)}
      >
        {layout.elements.map((element, index) => (
          <div
            key={index}
            role="button"
            tabIndex={0}
            aria-label={elementLabel(element)}
            aria-pressed={selected === index}
            className={`designer__block designer__block--${element.type}${
              selected === index ? ' is-selected' : ''
            }`}
            style={{
              left: element.x * scale,
              top: element.y * scale,
              width: element.w * scale,
              height: element.h * scale,
            }}
            onPointerDown={(event) => drag(event, index, 'move')}
            onKeyDown={(event) => nudge(event, index)}
          >
            <span className="designer__block-name">{elementLabel(element)}</span>
            {selected === index && (
              <span
                className="designer__handle"
                aria-label={t('labels.designer.resize')}
                onPointerDown={(event) => drag(event, index, 'resize')}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
